import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useWebSocket } from "../hooks/useWebSocket";
import { Ban, CreditCard, Loader2, MessageSquare, Hourglass, CheckCircle2, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const APPEAL_MIN_LEN = 30;

/**
 * Dashboard-level banner — only renders when the signed-in user is sanctioned.
 * Offers a Flutterwave-powered "Pay Fine" button to lift the block.
 * Handles the post-redirect `?fine_tx=...` verification automatically.
 */
export default function SanctionBanner() {
  const { t } = useTranslation();
  const { user, checkAuth } = useAuth();
  const [fine, setFine] = useState(null);
  const [paying, setPaying] = useState(false);

  const fetchFine = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/fines/pending`, { withCredentials: true });
      setFine(r.data);
    } catch {
      setFine(null);
    }
  }, [user]);

  const [appeal, setAppeal] = useState(null);
  const [appealOpen, setAppealOpen] = useState(false);
  const [appealMsg, setAppealMsg] = useState("");
  const [appealBusy, setAppealBusy] = useState(false);

  const fetchAppeal = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/appeals/me`, { withCredentials: true });
      setAppeal(r.data?.has_appeal ? r.data : null);
    } catch {
      setAppeal(null);
    }
  }, [user]);

  useEffect(() => { fetchFine(); fetchAppeal(); }, [fetchFine, fetchAppeal]);

  // Listen for `sanction_cleared` over WS so the user sees their banner
  // disappear (and a celebratory toast) the instant an admin decides — no
  // page refresh required. We only mount this listener while the banner
  // itself is rendered (i.e. user is currently sanctioned), so the WS
  // connection lifetime exactly matches the user's "waiting" window.
  const celebrated = useRef(false);
  const handleWsMessage = useCallback((msg) => {
    if (msg?.type !== "sanction_cleared") return;
    if (msg.user_id && user?.user_id && msg.user_id !== user.user_id) return;
    if (celebrated.current) return;
    celebrated.current = true;
    toast.success(t("sanction.lifted"), { duration: 6000 });
    checkAuth?.();
    fetchFine();
    fetchAppeal();
  }, [user, checkAuth, fetchFine, fetchAppeal, t]);
  useWebSocket(handleWsMessage);

  const submitAppeal = async () => {
    if (appealMsg.trim().length < APPEAL_MIN_LEN) {
      toast.error(t("sanction.appeal_min_error", { n: APPEAL_MIN_LEN }));
      return;
    }
    setAppealBusy(true);
    try {
      const r = await axios.post(`${API}/appeals`, { message: appealMsg.trim() }, { withCredentials: true });
      setAppeal(r.data);
      setAppealOpen(false);
      setAppealMsg("");
      toast.success(t("sanction.appeal_submitted"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("sanction.appeal_failed"));
    } finally {
      setAppealBusy(false);
    }
  };

  // Handle verify-after-redirect when Flutterwave sends the user back with ?fine_tx=&transaction_id=
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fineTx = params.get("fine_tx");
    const transactionId = params.get("transaction_id");
    if (!fineTx) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/fines/verify/${fineTx}`, {
          params: transactionId ? { transaction_id: transactionId } : {},
          withCredentials: true,
        });
        if (r.data.status === "paid") {
          toast.success(t("sanction.fine_paid"));
          await checkAuth?.();
          fetchFine();
        } else {
          toast.error(t("sanction.payment_not_confirmed"));
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || t("sanction.verify_failed"));
      } finally {
        // Strip the query params so a refresh doesn't re-verify
        const url = new URL(window.location.href);
        url.searchParams.delete("fine_tx");
        url.searchParams.delete("transaction_id");
        url.searchParams.delete("tx_ref");
        url.searchParams.delete("status");
        window.history.replaceState({}, "", url.toString());
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const payFine = async () => {
    setPaying(true);
    try {
      const r = await axios.post(`${API}/fines/initiate`, {}, { withCredentials: true });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error(t("sanction.could_not_start"));
        setPaying(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("sanction.payment_failed"));
      setPaying(false);
    }
  };

  if (!fine?.sanctioned) return null;

  const pendingAppeal = appeal?.status === "pending";
  const upheldAppeal = appeal?.status === "upheld";

  return (
    <>
      <div className="bg-red-600 text-white px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid="sanction-banner">
        <div className="flex items-center gap-2.5 min-w-0">
          <Ban className="w-5 h-5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-bold font-body">{t("sanction.title")}</p>
            <p className="text-[11px] opacity-90 font-body truncate">
              {pendingAppeal ? (
                <span data-testid="appeal-pending-text"><Hourglass className="w-3 h-3 inline mr-0.5" /> {t("sanction.appeal_pending")}</span>
              ) : upheldAppeal ? (
                <span data-testid="appeal-upheld-text"><XCircle className="w-3 h-3 inline mr-0.5" /> {t("sanction.appeal_denied")} {appeal.admin_notes ? `Admin: "${appeal.admin_notes.slice(0, 80)}"` : ""} {t("sanction.appeal_denied_pay")}</span>
              ) : (
                <>{t("sanction.fine_message", { amount: fine.amount.toLocaleString() })}</>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!pendingAppeal && (
            <button
              onClick={() => setAppealOpen(true)}
              className="bg-white/15 hover:bg-white/25 text-white px-3 py-1.5 rounded-sm text-xs font-bold font-body flex items-center gap-1.5 border border-white/30"
              data-testid="appeal-open-btn"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              {upheldAppeal ? t("sanction.appeal_again_btn") : t("sanction.appeal_btn")}
            </button>
          )}
          <button
            onClick={payFine}
            disabled={paying}
            className="bg-white text-red-700 hover:bg-gray-100 px-3 py-1.5 rounded-sm text-xs font-bold font-body flex items-center gap-1.5 disabled:opacity-60"
            data-testid="pay-fine-btn"
          >
            {paying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
            {t("sanction.pay_fine", { amount: fine.amount.toLocaleString() })}
          </button>
        </div>
      </div>

      <Dialog open={appealOpen} onOpenChange={setAppealOpen}>
        <DialogContent className="max-w-lg" data-testid="appeal-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-black tracking-tighter flex items-center gap-2">
              <MessageSquare className="w-5 h-5" /> {t("sanction.dialog_title")}
            </DialogTitle>
            <DialogDescription>
              {t("sanction.dialog_desc")}
            </DialogDescription>
          </DialogHeader>
          {appeal?.sanction_reason && (
            <div className="text-xs bg-red-50 border border-red-100 text-red-800 rounded-sm p-2.5 font-body">
              <strong className="font-bold">{t("sanction.sanction_reason")}</strong> {appeal.sanction_reason}
            </div>
          )}
          <div>
            <Textarea
              value={appealMsg}
              onChange={(e) => setAppealMsg(e.target.value)}
              placeholder={t("sanction.appeal_placeholder")}
              rows={6}
              className="font-body text-sm rounded-sm"
              data-testid="appeal-textarea"
              maxLength={2000}
            />
            <div className="flex justify-between items-center mt-1.5 text-[10px] font-body">
              <span className={appealMsg.trim().length < APPEAL_MIN_LEN ? "text-amber-600" : "text-gray-500"}>
                {t("sanction.chars_min", { n: appealMsg.trim().length, min: APPEAL_MIN_LEN })}
              </span>
              <span className="text-gray-400">{t("sanction.chars_max", { n: appealMsg.length })}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAppealOpen(false)} className="rounded-sm" data-testid="appeal-cancel">
              {t("common.cancel")}
            </Button>
            <Button
              onClick={submitAppeal}
              disabled={appealBusy || appealMsg.trim().length < APPEAL_MIN_LEN}
              className="bg-black hover:bg-gray-800 text-white rounded-sm"
              data-testid="appeal-submit"
            >
              {appealBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
              {t("sanction.submit_appeal")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
